import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { addDoc, collection, deleteDoc, doc, getDocs, query, serverTimestamp, updateDoc, where } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { LuMapPin, LuStar } from 'react-icons/lu';
import Badge from '../../components/ui/Badge';
import { Field, TextArea, TextInput, ToggleTile } from '../../components/form/FormControls';
import { db } from '../../config/firebaseConfig';
import { useTranslation } from '../../i18n/LanguageContext';
import RecordListModal from './components/RecordListModal';
import { customerName } from './customerColumns';

const EMPTY_ADDRESS = {
  title: '',
  address: '',
  city: '',
  district: '',
  postalCode: '',
  isDefault: false,
  notes: '',
};

// Newest first (records without a date keep their order)
const byCreatedDesc = (a, b) => {
  if (a.createdAt && b.createdAt) {
    const aTime = a.createdAt.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
    const bTime = b.createdAt.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
    return bTime - aTime;
  }
  return 0;
};

/** A customer's addresses (CustomerAddresses): list, add, edit, delete, default address. */
const CustomerAddressModal = ({ customer, onClose }) => {
  const { t } = useTranslation();
  const tRef = useRef(t);
  tRef.current = t;
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [values, setValues] = useState(EMPTY_ADDRESS);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchAddresses = useCallback(async () => {
    try {
      const snapshot = await getDocs(query(collection(db, 'CustomerAddresses'), where('customerId', '==', customer.id)));
      setAddresses(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })).sort(byCreatedDesc));
    } catch (error) {
      console.error('Error fetching addresses:', error);
      toast.error(tRef.current('customers.addresses.toast.loadError'));
    } finally {
      setLoading(false);
    }
  }, [customer.id]);

  useEffect(() => {
    fetchAddresses();
  }, [fetchAddresses]);

  const setValue = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const openForm = (mode, address) => {
    setValues(address ? { ...address } : EMPTY_ADDRESS);
    setErrors({});
    setEditing({ mode });
  };

  // Only one default address: the others lose the mark
  const clearOtherDefaults = async (keepId) => {
    const others = addresses.filter((address) => address.isDefault && address.id !== keepId);
    for (const address of others) {
      // eslint-disable-next-line no-await-in-loop
      await updateDoc(doc(db, 'CustomerAddresses', address.id), { isDefault: false });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const found = {};
    if (!String(values.title || '').trim()) found.title = t('dataTable.form.required');
    if (!String(values.address || '').trim()) found.address = t('dataTable.form.required');
    setErrors(found);
    if (Object.keys(found).length > 0) {
      toast.error(t('customers.addresses.required'));
      return;
    }

    const isEdit = editing.mode === 'edit';
    setSaving(true);
    try {
      if (values.isDefault) await clearOtherDefaults(isEdit ? values.id : null);
      if (isEdit) {
        await updateDoc(doc(db, 'CustomerAddresses', values.id), { ...values, updatedAt: serverTimestamp() });
        toast.success(t('customers.addresses.toast.updated'));
      } else {
        await addDoc(collection(db, 'CustomerAddresses'), {
          ...values,
          customerId: customer.id,
          customerName: customerName(customer),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        toast.success(t('customers.addresses.toast.added'));
      }
      setEditing(null);
      fetchAddresses();
    } catch (error) {
      console.error(isEdit ? 'Error updating address:' : 'Error adding address:', error);
      toast.error(t(isEdit ? 'customers.addresses.toast.updateError' : 'customers.addresses.toast.addError'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (address) => {
    try {
      await deleteDoc(doc(db, 'CustomerAddresses', address.id));
      toast.success(t('customers.addresses.toast.deleted'));
      await fetchAddresses();
    } catch (error) {
      console.error('Error deleting address:', error);
      toast.error(t('customers.addresses.toast.deleteError'));
    }
  };

  const field = (name, props = {}) => (
    <TextInput id={`address-${name}`} value={values[name] || ''} onChange={(event) => setValue(name, event.target.value)} error={errors[name]} {...props} />
  );

  const renderForm = () => (
    <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
      <Field label={t('customers.addresses.fields.title')} htmlFor="address-title" required error={errors.title}>
        {field('title', { placeholder: t('customers.addresses.fields.titlePlaceholder'), autoComplete: 'off' })}
      </Field>
      <Field label={t('customers.addresses.fields.postalCode')} htmlFor="address-postalCode">
        {field('postalCode', { inputMode: 'numeric', autoComplete: 'postal-code' })}
      </Field>
      <Field label={t('customers.addresses.fields.city')} htmlFor="address-city">
        {field('city', { autoComplete: 'address-level1' })}
      </Field>
      <Field label={t('customers.addresses.fields.district')} htmlFor="address-district">
        {field('district', { autoComplete: 'address-level2' })}
      </Field>
      <Field label={t('customers.addresses.fields.address')} htmlFor="address-address" required error={errors.address} className="sm:tw-col-span-2">
        <TextArea
          id="address-address"
          rows={3}
          value={values.address || ''}
          onChange={(event) => setValue('address', event.target.value)}
          placeholder={t('customers.addresses.fields.addressPlaceholder')}
          error={errors.address}
        />
      </Field>
      <Field label={t('customers.addresses.fields.notes')} htmlFor="address-notes">
        <TextArea
          id="address-notes"
          rows={2}
          value={values.notes || ''}
          onChange={(event) => setValue('notes', event.target.value)}
          placeholder={t('customers.addresses.fields.notesPlaceholder')}
        />
      </Field>
      <Field label={t('customers.addresses.makeDefault')} htmlFor="address-isDefault" hint={t('customers.addresses.makeDefaultHint')}>
        <ToggleTile
          id="address-isDefault"
          label={t('customers.addresses.default')}
          checked={Boolean(values.isDefault)}
          onChange={(checked) => setValue('isDefault', checked)}
        />
      </Field>
    </div>
  );

  const renderItem = (address) => {
    const place = [address.district, address.city].filter(Boolean).join(', ');
    return (
      <>
        <p className="tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-gap-2">
          <span className="tw-truncate tw-text-[15px] tw-font-semibold tw-text-slate-900 dark:tw-text-white">{address.title}</span>
          {address.isDefault && (
            <Badge tone="accent">
              <LuStar aria-hidden="true" className="tw-h-3 tw-w-3" />
              {t('customers.addresses.default')}
            </Badge>
          )}
        </p>
        <p className="tw-mt-1 tw-whitespace-pre-line tw-break-words tw-text-sm tw-text-slate-600 dark:tw-text-slate-300">{address.address}</p>
        {(place || address.postalCode) && (
          <p className="tw-mt-1 tw-text-xs tw-font-medium tw-text-slate-500 dark:tw-text-slate-400">
            {place}
            {address.postalCode && `${place ? ' - ' : ''}${address.postalCode}`}
          </p>
        )}
        {address.notes && (
          <p className="tw-mt-2 tw-whitespace-pre-line tw-break-words tw-rounded-lg tw-bg-slate-900/[0.03] tw-px-2.5 tw-py-1.5 tw-text-xs tw-text-slate-500 dark:tw-bg-white/[0.04] dark:tw-text-slate-400">
            {address.notes}
          </p>
        )}
      </>
    );
  };

  return (
    <RecordListModal
      title={t('customers.addresses.title')}
      subtitle={customerName(customer)}
      icon={LuMapPin}
      gradient="tw-from-violet-400 tw-to-purple-600 tw-shadow-violet-500/30"
      countLabel={t('customers.addresses.count', { count: addresses.length })}
      addLabel={t('customers.addresses.add')}
      emptyLabel={t('customers.addresses.empty')}
      emptyHint={t('customers.addresses.emptyHint')}
      deleteConfirmLabel={t('customers.addresses.deleteConfirm')}
      items={addresses}
      loading={loading}
      renderItem={renderItem}
      editing={editing}
      formTitle={editing?.mode === 'edit' ? t('customers.addresses.editTitle') : t('customers.addresses.addTitle')}
      renderForm={renderForm}
      saving={saving}
      onAdd={() => openForm('add')}
      onEdit={(address) => openForm('edit', address)}
      onDelete={handleDelete}
      onCancelForm={() => setEditing(null)}
      onSubmit={handleSubmit}
      onClose={onClose}
    />
  );
};

CustomerAddressModal.propTypes = {
  customer: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default CustomerAddressModal;
